import * as React from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cn } from '../../lib/utils';

const Tabs = TabsPrimitive.Root;

export type TabsChromeVariant = 'default' | 'segmented' | 'sidebar';

type TabsListProps = React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> & {
  variant?: TabsChromeVariant;
};

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  TabsListProps
>(({ className, variant = 'default', ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      variant === 'segmented' &&
        'flex h-auto min-h-11 w-full flex-wrap items-stretch justify-stretch gap-1 rounded-xl bg-surface-container-low/80 p-1 sm:flex-nowrap',
      variant === 'default' &&
        'inline-flex h-11 items-center justify-center rounded-lg bg-surface-container p-1',
      // Sidebar - vertical nav rail on desktop, horizontally scrollable pill row
      // on mobile. No chrome of its own (it's a navigation column, not a control).
      variant === 'sidebar' &&
        '-mx-1 flex gap-1 overflow-x-auto px-1 pb-1 md:mx-0 md:flex-col md:gap-0.5 md:overflow-visible md:px-0 md:pb-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
      className
    )}
    {...props}
  />
));
TabsList.displayName = TabsPrimitive.List.displayName;

type TabsTriggerProps = React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> & {
  variant?: TabsChromeVariant;
};

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  TabsTriggerProps
>(({ className, variant = 'default', ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      variant === 'segmented' &&
        [
          'inline-flex min-w-0 items-center justify-center whitespace-nowrap rounded-lg border border-transparent bg-transparent',
          'px-3 py-2 text-sm font-medium text-text-muted transition-colors duration-200',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary focus-visible:ring-offset-2 focus-visible:ring-offset-bg-primary',
          'disabled:pointer-events-none disabled:opacity-50',
          'data-[state=active]:border-accent-primary data-[state=active]:bg-surface-container data-[state=active]:font-semibold data-[state=active]:text-text-primary data-[state=active]:shadow-none',
          'data-[state=inactive]:hover:bg-surface-container-high/45 data-[state=inactive]:hover:text-text-secondary',
          'min-h-[44px] sm:min-h-[36px]',
        ].join(' '),
      variant === 'default' &&
        [
          'inline-flex min-h-[36px] items-center justify-center whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium text-text-muted transition-all duration-200',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary focus-visible:ring-offset-2 focus-visible:ring-offset-bg-primary',
          'disabled:pointer-events-none disabled:opacity-50',
          'data-[state=active]:bg-surface-container-low data-[state=active]:text-text-primary data-[state=active]:shadow-sm',
          'hover:bg-surface-container-high/50 hover:text-text-secondary',
        ].join(' '),
      variant === 'sidebar' &&
        [
          'group inline-flex min-h-[44px] w-auto cursor-pointer items-center justify-start gap-3 whitespace-nowrap rounded-lg px-3 py-2.5 text-sm font-medium text-text-muted transition-colors duration-200 md:w-full',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary focus-visible:ring-offset-2 focus-visible:ring-offset-bg-primary',
          'disabled:pointer-events-none disabled:opacity-50',
          '[&>svg]:h-[18px] [&>svg]:w-[18px] [&>svg]:shrink-0',
          'hover:bg-surface-container hover:text-text-primary',
          // Active: gold-tint background signals selection; label uses
          // text-primary (dark) for contrast - gold-on-gold-tint was ~1:1.
          'data-[state=active]:bg-accent-primary-muted data-[state=active]:font-semibold data-[state=active]:text-text-primary',
        ].join(' '),
      className
    )}
    {...props}
  />
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'mt-4 focus-visible:outline-none focus-visible:ring-2',
      'focus-visible:ring-accent-primary focus-visible:ring-offset-2',
      'data-[state=active]:animate-in data-[state=inactive]:animate-out',
      'data-[state=active]:fade-in-0 data-[state=inactive]:fade-out-0',
      className
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
